import {
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { fetchClosedChats } from "@/core/api/chatApi";
import { useAuth } from "@/core/hooks/useAuth";
import { baseQueryOptions } from "@/core/hooks/useApiQueries/baseQueryOptions";
import { realtimeQueryOptions } from "@/core/hooks/useApiQueries/realtimeQueryOptions";
import { useSectorConnection } from "@/core/providers/SectorConnectionProvider/context";
import { getNextOffset } from "@/shared/utils";
import { getPaginationParams } from "@/shared/utils/pagination";
import type { DocID } from "@/types";
import {
  CLOSED_NAV_COUNT_DAYS,
  closedChatsCountQueryKey,
  closedChatsQueryKey,
} from "@/features/chat/utils/closedChats";

/**
 * Lista de Encerradas (SA-1369): rota própria (`/closed`), ordenada pela data
 * do encerramento, com a paginação infinita das outras listas e o mesmo
 * filtro de tag. Só roda com a
 * lista à vista (`enabled`): fora dela ninguém a lê, e o total do menu tem a
 * sua contagem própria.
 */
export const useClosedChats = (
  sectorId: DocID | null | undefined,
  tagIds: DocID[],
  options?: { enabled?: boolean }
) => {
  const { user, token } = useAuth();
  const { connected } = useSectorConnection();

  return useInfiniteQuery({
    queryKey: closedChatsQueryKey(sectorId, tagIds),
    initialPageParam: 0,
    queryFn: async ({ pageParam = 0 }) => {
      if (!user) throw new Error("User not authenticated");
      if (!sectorId) throw new Error("Sector ID is required");
      return fetchClosedChats(token, sectorId, pageParam, { tagIds });
    },
    getNextPageParam: (lastPage) => {
      const { start, limit, total } = getPaginationParams(
        lastPage.meta.pagination
      );
      return getNextOffset(start, limit, total);
    },
    enabled: !!user && !!sectorId && connected && (options?.enabled ?? true),
    ...realtimeQueryOptions,
  });
};

/** Mesmo formato vazio da contagem de abertas (ver `useOpenChatsCount`). */
type ClosedChatsCountData = {
  pages: { data: never[]; meta: { pagination: { total: number } } }[];
  pageParams: number[];
};

/** Query da contagem de Encerradas, para o hook e para o boot (SA-1452). */
export const closedChatsCountQueryOptions = ({
  sectorId,
  token,
}: {
  sectorId: DocID | null | undefined;
  token: string | undefined;
}) =>
  queryOptions({
    queryKey: closedChatsCountQueryKey(sectorId),
    queryFn: async (): Promise<ClosedChatsCountData> => {
      if (!sectorId) throw new Error("Sector ID is required");
      const response = await fetchClosedChats(token, sectorId, 0, {
        days: CLOSED_NAV_COUNT_DAYS,
        limit: 1,
      });
      return {
        pages: [
          {
            data: [],
            meta: {
              pagination: { total: response.meta?.pagination?.total ?? 0 },
            },
          },
        ],
        pageParams: [0],
      };
    },
    ...baseQueryOptions,
    // Mesmo raciocínio da contagem de abertas: abrir e recolher o menu não
    // refaz a request; um minuto cobre um evento perdido.
    staleTime: 60 * 1000,
    refetchOnMount: true,
  });

/**
 * Total de Encerradas dos últimos 7 dias, com a visibilidade do servidor: o
 * número do sub-item "Encerradas" do menu. Uma request de 1 linha
 * (`days=7&limit=1&withCount`); refaz com a invalidação de `["chats"]` e
 * quando o socket vê uma conversa entrar ou sair de `closed`. Setor sem
 * WhatsApp não mostra número (ver `useOpenChatsCount`).
 */
export function useClosedChatsCount(
  sectorId: DocID | null | undefined
): number | null {
  const { user, token } = useAuth();
  const { connected } = useSectorConnection();

  const query = useQuery({
    ...closedChatsCountQueryOptions({ sectorId, token }),
    select: (data) => data.pages[0]?.meta.pagination.total ?? 0,
    enabled: !!user?.documentId && !!sectorId && connected,
  });

  if (!connected) return null;
  return query.data ?? null;
}
